/** @jsxFrag React.Fragment */

/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';
import { graphql } from 'react-apollo';
import { Link as ReactRouterLink } from 'react-router';

import gql from 'graphql-tag';
import { branch, compose, withStateHandlers } from 'recompose';

import { isAuthenticatedUser } from 'js/lib/user';

import { InlineNotification, Link, Typography } from '@coursera/cds-core';
import type { LinkProps } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import { Section } from 'bundles/enterprise-legacy-learner-home/components/AutoHeading';
import DetailedObjectivesInformationLink from 'bundles/enterprise-legacy-learner-home/components/StaticInformationCards/DetailedObjectivesInformationLink';
import SkillSetFiltersHOC from 'bundles/enterprise-legacy-learner-home/components/single-program/SkillSetFiltersHOC';
import type { PropsFromSkillSetFiltersHOC } from 'bundles/enterprise-legacy-learner-home/components/single-program/SkillSetFiltersHOC';
import { toProgramHomeTab } from 'bundles/enterprise-legacy-learner-home/links';
import filterExistsOrDefault from 'bundles/enterprise-legacy-learner-home/utils/filterExistsOrDefault';
import withPreventDefault from 'bundles/enterprise-legacy-learner-home/utils/withPreventDefault';
import SkillSetDiscoveryHeader from 'bundles/program-home/components/multiprogram/SkillSetDiscoveryHeader';
import SkillSetInformationCard from 'bundles/program-home/components/multiprogram/SkillSetInformationCard';
import { SkillSetsGrid, SkillSetsGridPlaceholder } from 'bundles/program-home/components/multiprogram/SkillSetsGrid';
import type {
  ObjectiveListQuery as ObjectiveListQueryData,
  ObjectiveListQueryVariables,
  ObjectiveListQuery_EnterpriseTargetSkillProfileSummariesV1Resource_byProgram_elements as TargetSkillProficiencySummary,
} from 'bundles/program-home/components/multiprogram/__generated__/ObjectiveListQuery';
import { TabName } from 'bundles/unified-common/constants/enterprise';

import _t from 'i18n!nls/program-home';

import 'css!./__styles__/SkillSetCards';

type PropsFromCaller = {
  programId: string;
  showSkillSets?: () => void;
  programSlug: string;
  programName?: string;
  title: string;
  skillSetLength?: number;
  renderOrgHomeVariant?: boolean;
  isAnAcademicDisciplineProgram?: boolean;
  showCarousel?: boolean;
  showSkillSetRoleFilter: boolean;
};

type PropsFromGraphql = {
  skillsets: TargetSkillProficiencySummary[];
  error: boolean;
  loading: boolean;
};

type State = {
  helpCardId: string;
  helpIsExpanded: boolean;
};

type StateHandlers = {
  toggleHelpIsExpanded: () => State;
};

// TODO(ppaskaris): clean up these props
type PropsForSkillSetDiscoveryPlaceholder = {
  showSkillSets?: () => void;
  programSlug: string;
  programName?: string;
  toggleHelpIsExpanded?: () => State;
  helpIsExpanded?: boolean;
  skillSetLength?: number;
  title?: string;
  renderOrgHomeVariant?: boolean;
  isAnAcademicDisciplineProgram?: boolean;
  showSkillSetRoleFilter: boolean;
  showCarousel?: boolean;
};

type PropsForSkillSetDiscovery = {
  programSlug: string;
  programName?: string;
  showSkillSets?: () => void;
  helpIsExpanded: boolean;
  toggleHelpIsExpanded: () => State;
  helpCardId: string;
  objectives: TargetSkillProficiencySummary[];
  title?: string;
  renderOrgHomeVariant?: boolean;
  totalSkillSetLength?: number;
  isAnAcademicDisciplineProgram?: boolean;
  showSkillSetRoleFilter: boolean;
  showCarousel?: boolean;
} & Omit<PropsFromSkillSetFiltersHOC, 'isFilterSelected'>;

type PropsForSkillSetTabLink = {
  showSkillSets?: () => void;
  programSlug: string;
  skillSetLength?: number;
};

type PropsFromSkillSetDiscoveryFilters = {
  skillsets?: TargetSkillProficiencySummary[];
} & PropsFromSkillSetFiltersHOC;

type Props = PropsFromCaller & PropsFromGraphql & State & StateHandlers & PropsFromSkillSetDiscoveryFilters;

export const ObjectiveListQuery = gql`
  query ObjectiveListQuery($programId: String!, $limit: Int) {
    EnterpriseTargetSkillProfileSummariesV1Resource {
      byProgram(id: $programId, limit: $limit) {
        elements {
          id
          slug
          title
          occupationId
          goalType
          targetSkillProficiencies {
            skillId
            skillName
          }
        }
      }
    }
  }
`;

const TrackedLink = withSingleTracked({ type: 'BUTTON' })<LinkProps<ReactRouterLink>>(
  // TODO(ppaskaris): Ask how to type this.
  // @ts-expect-error
  Link
);

const SkillsDiscoverySkillSetsTabLink = ({ showSkillSets, programSlug, skillSetLength }: PropsForSkillSetTabLink) => {
  return (
    <div className="skillset-tab-link-container">
      {/* eslint-disable-next-line @coursera-web/custom-rules/prefer-cds-typography2 -- FIXME: legacy; migrate to Typography2 */}
      <Typography variant="h2semibold" className="skillset-tab-prefix" component="span">
        {_t('Want to see more? ')}
      </Typography>
      <TrackedLink
        {...Link.defaultProps}
        component={ReactRouterLink}
        className="skillset-tab-link"
        trackingName="view_all_skillsets_footer_link"
        to={toProgramHomeTab(programSlug, TabName.SkillSets)}
        aria-label={_t('View all SkillSets in this program')}
        typographyVariant="subtitleLarge"
        variant="quiet"
        onClick={showSkillSets ? withPreventDefault(showSkillSets) : undefined}
        icon={<ArrowNextIcon className="skillset-tab-link-icon" size="medium" />}
        iconPosition="after"
      >
        {skillSetLength && skillSetLength > 6
          ? _t('View all SkillSets')
          : _t('View more details for each of these SkillSets')}
      </TrackedLink>
    </div>
  );
};

const SkillSetDiscoveryPlaceholder = ({
  showSkillSets,
  programSlug,
  helpIsExpanded,
  toggleHelpIsExpanded,
  skillSetLength,
  renderOrgHomeVariant,
}: PropsForSkillSetDiscoveryPlaceholder) => {
  return (
    <div className="rc-SkillSetCards" aria-hidden>
      <SkillSetDiscoveryHeader />
      {!renderOrgHomeVariant && typeof helpIsExpanded === 'boolean' && typeof toggleHelpIsExpanded === 'function' && (
        <DetailedObjectivesInformationLink
          rootClassName="skillset-discovery-HelpLink"
          cardHtmlId="ExploreSkillsListInformationCard"
          isOpen={helpIsExpanded}
          onClick={toggleHelpIsExpanded}
          enableSkillsDiscovery
          isCdsEnabled
        />
      )}
      {!renderOrgHomeVariant && typeof helpIsExpanded === 'boolean' && (
        <SkillSetInformationCard
          rootClassName="skillset-information-card"
          id="ExploreSkillsListInformationCard"
          isOpen={helpIsExpanded}
        />
      )}
      <SkillSetsGridPlaceholder skillSetLength={skillSetLength} />
      {!renderOrgHomeVariant && (
        <SkillsDiscoverySkillSetsTabLink
          showSkillSets={showSkillSets}
          programSlug={programSlug}
          skillSetLength={skillSetLength}
        />
      )}
    </div>
  );
};

const SkillSetDiscoveryCards = ({
  programSlug,
  showSkillSets,
  helpIsExpanded,
  helpCardId,
  objectives,
  renderOrgHomeVariant,
  totalSkillSetLength,
}: PropsForSkillSetDiscovery) => {
  return (
    <div className="rc-SkillSetCards">
      <SkillSetDiscoveryHeader />
      <Section initialLevel={3}>
        {!renderOrgHomeVariant && (
          <SkillSetInformationCard rootClassName="skillset-information-card" id={helpCardId} isOpen={helpIsExpanded} />
        )}
        {objectives && <SkillSetsGrid objectives={objectives} programSlug={programSlug} />}
        {!renderOrgHomeVariant && (
          <SkillsDiscoverySkillSetsTabLink
            showSkillSets={showSkillSets}
            programSlug={programSlug}
            skillSetLength={totalSkillSetLength}
          />
        )}
      </Section>
    </div>
  );
};

export const SkillSetCardsRenderer = ({
  error,
  loading,
  showSkillSets,
  helpCardId,
  helpIsExpanded,
  toggleHelpIsExpanded,
  programSlug,
  programName,
  title,
  skillSetLength,
  renderOrgHomeVariant,
  skillsets,
  filterOptions,
  filterSelections,
  allOccupations,
  skillNames,
  onRoleChange,
  onSkillsChange,
  onGoalChange,
  onFilterReset,
  onFilterInputChange,
  isAnAcademicDisciplineProgram,
  showSkillSetRoleFilter,
  showCarousel = false,
}: Props) => {
  if (loading) {
    return (
      <SkillSetDiscoveryPlaceholder
        showSkillSetRoleFilter={showSkillSetRoleFilter}
        helpIsExpanded={helpIsExpanded}
        toggleHelpIsExpanded={toggleHelpIsExpanded}
        showSkillSets={showSkillSets}
        programSlug={programSlug}
        programName={programName}
        skillSetLength={skillSetLength}
        title={title}
        renderOrgHomeVariant={renderOrgHomeVariant}
        isAnAcademicDisciplineProgram={isAnAcademicDisciplineProgram}
        showCarousel={showCarousel}
      />
    );
  }
  if (error) {
    return (
      <div className="rc-SkillSetCards">
        <InlineNotification severity="error" role="alert">
          {_t('Sorry! Something went wrong. Please refresh the page.')}
        </InlineNotification>
      </div>
    );
  }
  return (
    <SkillSetDiscoveryCards
      showSkillSetRoleFilter={showSkillSetRoleFilter}
      programSlug={programSlug}
      programName={programName}
      showSkillSets={showSkillSets}
      helpIsExpanded={helpIsExpanded}
      toggleHelpIsExpanded={toggleHelpIsExpanded}
      helpCardId={helpCardId}
      objectives={skillsets}
      renderOrgHomeVariant={renderOrgHomeVariant}
      filterOptions={filterOptions}
      filterSelections={filterSelections}
      allOccupations={allOccupations}
      skillNames={skillNames}
      onRoleChange={onRoleChange}
      onSkillsChange={onSkillsChange}
      onGoalChange={onGoalChange}
      onFilterReset={onFilterReset}
      onFilterInputChange={onFilterInputChange}
      totalSkillSetLength={skillSetLength}
      title={title}
      isAnAcademicDisciplineProgram={isAnAcademicDisciplineProgram}
      showCarousel={showCarousel}
    />
  );
};

export const enhance = compose<Props, PropsFromCaller>(
  graphql<PropsFromCaller, ObjectiveListQueryData, ObjectiveListQueryVariables, PropsFromGraphql>(ObjectiveListQuery, {
    options: ({ programId, renderOrgHomeVariant }) => ({
      ssr: false,
      variables: {
        programId,
        limit: renderOrgHomeVariant ? 3 : null,
      },
    }),
    props: ({ data }) => {
      const error = !!data?.error;
      const skillsets = filterExistsOrDefault(
        data?.EnterpriseTargetSkillProfileSummariesV1Resource?.byProgram?.elements
      );
      return { skillsets, error, loading: data?.loading ?? true };
    },
  }),
  branch(
    ({ renderOrgHomeVariant }: PropsFromCaller) => !renderOrgHomeVariant && isAuthenticatedUser(),
    SkillSetFiltersHOC
  ),
  withStateHandlers<State, StateHandlers, PropsFromCaller>(
    {
      helpCardId: 'ExploreSkillsListInformationCard',
      helpIsExpanded: false,
    },
    {
      toggleHelpIsExpanded:
        ({ helpIsExpanded }) =>
        () => ({
          helpIsExpanded: !helpIsExpanded,
        }),
    }
  )
);

type SkillSetCardsComponentType = React.ComponentClass<PropsFromCaller> & {
  Placeholder: typeof SkillSetDiscoveryPlaceholder;
};

// Trick to avoid interop error between hooks/recompose, specifically:
//   Error: Invalid hook call. Hooks can only be called inside of the body of a function component.
const SkillSetCardsHookProxy = (props: Props) => <SkillSetCardsRenderer {...props} />;

const SkillSetCards: SkillSetCardsComponentType = Object.assign(enhance(SkillSetCardsHookProxy), {
  Placeholder: SkillSetDiscoveryPlaceholder,
});

export default SkillSetCards;
